/**
 * UI - Elegant Consent Modal
 * Minimalist, GDPR-compliant design
 */

export class UI {
  constructor(manager, config, t) {
    this.manager = manager;
    this.config = config;
    this.t = t;

    this.modal = null;
    this.settingsModal = null;
    this.floatingButton = null;
    this.toastObserver = null;
    this.toastOffsetTimer = null;
    this.updateToastOffset = this.updateToastOffset.bind(this);
    this.scheduleToastOffset = this.scheduleToastOffset.bind(this);
  }

  /**
   * Show the first-layer consent toast.
   */
  showBanner() {
    if (this.modal) return;

    this.modal = document.createElement('div');
    this.modal.className = `consent consent--${this.config.position}`;
    this.isModal = this.config.position === 'center';
    if (this.isModal) {
      // Skutečný modal: dialog sémantika, zámek scrollu, focus trap (jen centrovaný režim)
      this.modal.setAttribute('role', 'dialog');
      this.modal.setAttribute('aria-modal', 'true');
      this.modal.setAttribute('aria-labelledby', 'consent-banner-title');
    } else {
      this.modal.setAttribute('role', 'region');
      this.modal.setAttribute('aria-label', this.t.bannerTitle);
    }

    this.modal.innerHTML = `
      <div class="consent__backdrop"></div>
      <div class="consent__card">
        <button class="consent__close" data-consent-close aria-label="Close">×</button>

        <div class="consent__summary">
          <div>
            <h2 class="consent__title" id="consent-banner-title">${this.t.bannerTitle}</h2>
            <p class="consent__text">${this.t.bannerText}</p>
          </div>
        </div>

        <div class="consent__actions">
          <button class="consent__btn consent__btn--reject" data-consent-reject>
            ${this.t.rejectAll}
          </button>
          <button class="consent__btn consent__btn--accept" data-consent-accept>
            ${this.t.acceptAll}
          </button>
        </div>

        <div class="consent__footer">
          <button class="consent__link" data-consent-settings>${this.t.settings}</button>
          <span class="consent__separator">|</span>
          <a href="${this.config.policyUrl}" class="consent__link" target="_blank" rel="noopener">${this.t.learnMore}</a>
        </div>
      </div>
    `;

    // Event: Accept
    this.modal.querySelector('[data-consent-accept]').addEventListener('click', () => {
      this.manager.acceptAll();
      this.hideModal();
      this.createFloatingButton();
    });

    // Event: Reject
    this.modal.querySelector('[data-consent-reject]').addEventListener('click', () => {
      this.manager.rejectAll();
      this.hideModal();
      this.createFloatingButton();
    });

    // Event: Close (X) = postpone (no choice stored, ask again next visit)
    this.modal.querySelector('[data-consent-close]').addEventListener('click', () => {
      this.manager.postpone('close');
      this.hideModal();
      this.createFloatingButton();
    });

    // Event: Backdrop click = postpone
    this.modal.querySelector('.consent__backdrop').addEventListener('click', () => {
      this.manager.postpone('backdrop');
      this.hideModal();
      this.createFloatingButton();
    });

    // Event: Settings
    this.modal.querySelector('[data-consent-settings]').addEventListener('click', () => {
      this.showSettings();
    });

    // Apply primary color
    this.modal.style.setProperty('--consent-primary', this.config.primaryColor);

    // Event: Esc on main banner = postpone (same as X); ignored while settings modal is open
    this.escHandler = (e) => {
      if (e.key !== 'Escape' || !this.modal || this.settingsModal) return;
      this.manager.postpone('esc');
      this.hideModal();
      this.createFloatingButton();
    };
    document.addEventListener('keydown', this.escHandler);

    document.body.appendChild(this.modal);
    if (this.isModal) this.activateModalMode();
    this.manager.emitBannerShow();
    this.watchToastOffset();

    // Animate in
    requestAnimationFrame(() => {
      this.modal.classList.add('consent--visible');
      // visibility přechází hidden → visible plynule, focus přijme až o chvíli později
      window.setTimeout(() => this.focusModal(), 60);
    });
  }

  /**
   * Hide consent modal
   */
  hideModal() {
    if (!this.modal) return;

    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }
    this.modal.classList.remove('consent--visible');
    this.deactivateModalMode();
    this.stopWatchingToastOffset();

    setTimeout(() => {
      this.modal?.remove();
      this.modal = null;
    }, 400);
  }

  /** Zámek scrollu + focus trap hlavního modalu. */
  activateModalMode() {
    const html = document.documentElement;
    const body = document.body;
    this.lockState = {
      htmlOverflow: html.style.overflow,
      bodyPaddingRight: body.style.paddingRight,
      bodyOverflow: body.style.overflow,
      opener: document.activeElement,
    };
    const scrollbar = window.innerWidth - html.clientWidth;
    if (scrollbar > 0) {
      const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${current + scrollbar}px`;
    }
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.classList.add('consent-lock');

    this.trapHandler = (e) => {
      if (e.key !== 'Tab' || !this.modal || this.settingsModal) return;
      const items = this.focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!this.modal.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this.trapHandler, true);

    // Focus až po zviditelnění (visibility:hidden prvek focus nepřijme)
    this.modal.setAttribute('tabindex', '-1');
  }

  /** Přesune focus do dialogu (první tlačítko). Volá se po přidání třídy viditelnosti. */
  focusModal() {
    if (!this.modal || !this.isModal) return;
    const target = this.modal.querySelector('[data-consent-reject]') || this.modal;
    target.focus({ preventScroll: true });
  }

  focusables() {
    return Array.from(this.modal.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])'))
      .filter((el) => !el.disabled && el.offsetParent !== null);
  }

  /** Vrátí scroll i focus přesně do původního stavu. */
  deactivateModalMode() {
    if (!this.lockState) return;
    const html = document.documentElement;
    const body = document.body;
    const state = this.lockState;
    this.lockState = null;
    html.style.overflow = state.htmlOverflow;
    body.style.overflow = state.bodyOverflow;
    body.style.paddingRight = state.bodyPaddingRight;
    html.classList.remove('consent-lock');
    if (this.trapHandler) {
      document.removeEventListener('keydown', this.trapHandler, true);
      this.trapHandler = null;
    }
    const opener = state.opener;
    if (opener && opener !== document.body && document.contains(opener) && typeof opener.focus === 'function') {
      opener.focus({ preventScroll: true });
    }
  }

  /** Keep the toast above the landing page's sticky buy bar. */
  updateToastOffset() {
    if (!this.modal) return;

    let offset = 16;
    document.querySelectorAll('.ml-buybar.is-visible, [data-consent-bottom-obstacle]').forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        offset = Math.max(offset, Math.ceil(window.innerHeight - rect.top + 12));
      }
    });
    this.modal.style.setProperty('--consent-offset', `${offset}px`);
  }

  scheduleToastOffset() {
    // Landing scripts toggle the sticky buy bar during their own scroll
    // callback. Run after that callback has applied its class and transform.
    requestAnimationFrame(() => requestAnimationFrame(this.updateToastOffset));
  }

  watchToastOffset() {
    this.updateToastOffset();
    this.toastOffsetTimer = window.setInterval(this.updateToastOffset, 250);
    window.addEventListener('scroll', this.scheduleToastOffset, { passive: true });
    window.addEventListener('resize', this.scheduleToastOffset, { passive: true });
    this.toastObserver = new MutationObserver((records) => {
      if (records.some((record) => record.target.matches?.('.ml-buybar, [data-consent-bottom-obstacle]'))) {
        this.scheduleToastOffset();
      }
    });
    this.toastObserver.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  stopWatchingToastOffset() {
    window.removeEventListener('scroll', this.scheduleToastOffset);
    window.removeEventListener('resize', this.scheduleToastOffset);
    this.toastObserver?.disconnect();
    this.toastObserver = null;
    window.clearInterval(this.toastOffsetTimer);
    this.toastOffsetTimer = null;
  }

  // Alias for compatibility
  hideBanner() {
    this.hideModal();
  }

  /**
   * Show settings modal
   */
  showSettings() {
    if (this.settingsModal) return;

    const consent = this.manager.getConsent();

    this.settingsModal = document.createElement('div');
    this.settingsModal.className = 'consent-settings';
    this.settingsModal.setAttribute('role', 'dialog');
    this.settingsModal.setAttribute('aria-modal', 'true');
    this.settingsModal.setAttribute('aria-label', this.t.settingsTitle);

    this.settingsModal.innerHTML = `
      <div class="consent-settings__backdrop" data-close></div>
      <div class="consent-settings__card">
        <button class="consent-settings__close" data-close aria-label="Close">×</button>

        <h2 class="consent-settings__title">${this.t.settingsTitle}</h2>
        <p class="consent-settings__intro">${this.t.settingsIntro}</p>

        <div class="consent-settings__categories">
          <div class="consent-settings__category">
            <div class="consent-settings__row">
              <div class="consent-settings__info">
                <span class="consent-settings__name">${this.t.necessary}</span>
                <span class="consent-settings__badge">${this.t.alwaysActive}</span>
              </div>
              <label class="consent-settings__toggle consent-settings__toggle--disabled">
                <input type="checkbox" checked disabled>
                <span class="consent-settings__slider"></span>
              </label>
            </div>
            <p class="consent-settings__desc">${this.t.necessaryDesc}</p>
          </div>

          <div class="consent-settings__category">
            <div class="consent-settings__row">
              <div class="consent-settings__info">
                <span class="consent-settings__name">${this.t.analytics}</span>
              </div>
              <label class="consent-settings__toggle">
                <input type="checkbox" ${consent.analytics ? 'checked' : ''} data-category="analytics">
                <span class="consent-settings__slider"></span>
              </label>
            </div>
            <p class="consent-settings__desc">${this.t.analyticsDesc}</p>
          </div>

          <div class="consent-settings__category">
            <div class="consent-settings__row">
              <div class="consent-settings__info">
                <span class="consent-settings__name">${this.t.marketing}</span>
              </div>
              <label class="consent-settings__toggle">
                <input type="checkbox" ${consent.marketing ? 'checked' : ''} data-category="marketing">
                <span class="consent-settings__slider"></span>
              </label>
            </div>
            <p class="consent-settings__desc">${this.t.marketingDesc}</p>
          </div>
        </div>

        <div class="consent-settings__actions">
          <button class="consent-settings__btn consent-settings__btn--save" data-save>
            ${this.t.saveSettings}
          </button>
          <button class="consent-settings__btn consent-settings__btn--accept" data-accept>
            ${this.t.acceptAll}
          </button>
        </div>
      </div>
    `;

    // Event: Close
    this.settingsModal.querySelectorAll('[data-close]').forEach(el => {
      el.addEventListener('click', () => this.hideSettings());
    });

    // Event: Save
    this.settingsModal.querySelector('[data-save]').addEventListener('click', () => {
      const choices = {
        analytics: this.settingsModal.querySelector('[data-category="analytics"]').checked,
        marketing: this.settingsModal.querySelector('[data-category="marketing"]').checked,
      };
      this.manager.saveCustom(choices);
      this.hideSettings();
      this.hideModal();
      this.createFloatingButton();
    });

    // Event: Accept All
    this.settingsModal.querySelector('[data-accept]').addEventListener('click', () => {
      this.manager.acceptAll();
      this.hideSettings();
      this.hideModal();
      this.createFloatingButton();
    });

    // Escape key
    this.settingsEscHandler = (e) => {
      if (e.key === 'Escape') this.hideSettings();
    };
    document.addEventListener('keydown', this.settingsEscHandler);

    // Apply primary color
    this.settingsModal.style.setProperty('--consent-primary', this.config.primaryColor);

    document.body.appendChild(this.settingsModal);

    // Animate in
    requestAnimationFrame(() => {
      this.settingsModal.classList.add('consent-settings--visible');
    });
  }

  /**
   * Hide settings modal
   */
  hideSettings() {
    if (!this.settingsModal) return;

    document.removeEventListener('keydown', this.settingsEscHandler);
    this.settingsModal.classList.remove('consent-settings--visible');

    setTimeout(() => {
      this.settingsModal?.remove();
      this.settingsModal = null;
    }, 400);
  }

  // Alias for showSettings
  showModal() {
    this.showSettings();
  }

  /**
   * Create floating button
   */
  createFloatingButton() {
    if (this.floatingButton) return;

    this.floatingButton = document.createElement('button');
    this.floatingButton.className = 'consent-fab';
    if (this.config.floatingPosition === 'right') {
      this.floatingButton.classList.add('consent-fab--right');
    }
    this.floatingButton.setAttribute('aria-label', this.t.settings);
    this.floatingButton.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="12" cy="12" r="10"/>
        <circle cx="8" cy="9" r="1.5" fill="currentColor"/>
        <circle cx="15" cy="10" r="1" fill="currentColor"/>
        <circle cx="10" cy="14" r="1" fill="currentColor"/>
        <circle cx="14" cy="15" r="0.75" fill="currentColor"/>
      </svg>
    `;

    this.floatingButton.addEventListener('click', () => {
      this.showSettings();
    });

    this.floatingButton.style.setProperty('--consent-primary', this.config.primaryColor);

    // Custom bottom position
    if (this.config.fabBottom) {
      this.floatingButton.style.setProperty('--consent-fab-bottom', this.config.fabBottom);
    }
    if (this.config.fabBottomMobile) {
      this.floatingButton.style.setProperty('--consent-fab-bottom-mobile', this.config.fabBottomMobile);
    }

    document.body.appendChild(this.floatingButton);

    requestAnimationFrame(() => {
      this.floatingButton.classList.add('consent-fab--visible');
    });
  }

  /**
   * Remove floating button
   */
  removeFloatingButton() {
    if (this.floatingButton) {
      this.floatingButton.remove();
      this.floatingButton = null;
    }
  }
}
